import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../services/api";
import { useQuery } from "@tanstack/react-query";
import { certificationsData } from "./data";
import { useApp } from "../../../services/app-context";
import { isCertificationModeEnabled } from "../../../integrations/posthog/featureFlags";
import { ShowToast } from "../../../services/toast";
import { getLoggerRef } from "../../../integrations/logger/logger-bridge";
import { LottieLoading } from "../../../components/Loading/LottieLoading";

export const CertificationContext = React.createContext({});

// used to fetch all the certifications, users and other data
export function CertificationProvider({ children }) {
  const { fetchCertificationAssets, isCertificationAssetsLoading, setIsCertificationAssetsLoading } = useApp();
  const [selectedCertificationAssignment, setSelectedCertificationAssignment] =
    useState<any>(null); // null if no assignment is selected
  const [selectedCertificationAttempt, setSelectedCertificationAttempt] =
    useState<any>(null); // null if no attempt is selected

    const { isLoading: isLoadingAttempts, data: certificationAttempts, refetch: refetchAttempts } = useQuery({
      queryKey: ["certification-attempts", selectedCertificationAssignment?.id],
      queryFn: () => api.getCertificationAttempts(selectedCertificationAssignment?.id),
      enabled: !!selectedCertificationAssignment?.id,
  });

  const { data: userData, isLoading: isUserLoading, isFetching: isUserFetching } = useQuery({
    queryKey: ["search-users"],
    queryFn: () => api.searchUsers(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["certifications"],
    queryFn: () => api.searchCertifications({}),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const users = userData?.users || [];
  const certifications = data?.data?.certifications || [];

  const attempts = useMemo(() => {
    return certificationAttempts?.data?.attempts || [certificationsData];
}, [certificationAttempts]);

  useEffect(() => {
    if (!isCertificationModeEnabled()) {
      setIsCertificationAssetsLoading(false);
      return;
    }
    const load = async () => {
      try {
        await fetchCertificationAssets();
      } catch {
        ShowToast({
          message: "Something went wrong while fetching certification data",
          type: "error",
        });
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; fetchCertificationAssets identity changes when app context updates and would cause a loop
  }, []);

  const values = {
    certifications,
    users,
    selectedCertificationAssignment,
    setSelectedCertificationAssignment,
    selectedCertificationAttempt,
    setSelectedCertificationAttempt,
    certificationsLoading: isLoading || isFetching,
    userLoading: isUserLoading || isUserFetching,
    refetchCertifications: refetch,
    attempts,
    isLoadingAttempts,
    refetchAttempts: refetchAttempts,
  };
  if (isCertificationAssetsLoading) {
    return <LottieLoading />;
  }
  return (
    <CertificationContext.Provider value={values}>
      {children}
    </CertificationContext.Provider>
  );
}

export function useCertification(): {
  certifications: any;
  setCertifications: React.Dispatch<React.SetStateAction<any>>;
  users: any;
  setUsers: React.Dispatch<React.SetStateAction<any>>;
  selectedCertificationAssignment: any;
  setSelectedCertificationAssignment: React.Dispatch<React.SetStateAction<any>>;
  selectedCertificationAttempt: any;
  setSelectedCertificationAttempt: React.Dispatch<React.SetStateAction<any>>;
  certificationsLoading: boolean;
  userLoading: boolean;
  refetchCertifications: () => void;
  attempts: any[];
  isLoadingAttempts: boolean;
  refetchAttempts: () => void;
} {
  const context = React.useContext(CertificationContext);
  if (context === undefined) {
    throw new Error(
      "useCertification must be used within a CertificationProvider"
    );
  }
  return context as {
    certifications: any;
    setCertifications: React.Dispatch<React.SetStateAction<any>>;
    users: any;
    setUsers: React.Dispatch<React.SetStateAction<any>>;
    selectedCertificationAssignment: any;
    setSelectedCertificationAssignment: React.Dispatch<
      React.SetStateAction<any>
    >;
    selectedCertificationAttempt: any;
    setSelectedCertificationAttempt: React.Dispatch<
      React.SetStateAction<any>
    >;
    certificationsLoading: boolean;
    userLoading: boolean;
    refetchCertifications: () => void;
    attempts: any[];
    isLoadingAttempts: boolean;
    refetchAttempts: () => void;
  };
}
